/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react'
import { Box, Button, Typography, CircularProgress } from '@mui/material'

import type {
  UserAssignmentsViewModel as ApiUserAssignmentsViewModel,
  AssignmentViewModel as ApiAssignmentViewModel,
} from '@rsmus/ecp-userservice'

import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import api from '../../api'
import { Styles } from '../../types'
import ResponsiveModal from '../../rsmCoreComponents/components/ResponsiveModal'
import TextField from '../TextField/TextField'
import { tokens } from '../../styles/materialTheme'
import UserAssignment from './UserAssignment'

export interface UserAssignmentModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  onError: () => void
}

interface UserAssignmentFormData {
  firstName: string
  lastName: string
  email: string
  jobTitle: string
}

const styles: Styles = {
  modalContainer: {
    padding: '2rem',
    width: '100%',
    maxWidth: '60rem',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  header: {
    fontFamily: 'Prelo-Bold',
    fontSize: '1.5rem',
    marginBottom: '1rem',
  },
  subheader: {
    fontFamily: 'Prelo-Book, sans-serif',
    fontSize: '1rem',
    marginBottom: '1.5rem',
    color: 'text.primary',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  fieldRow: {
    display: 'flex',
    gap: '1rem',
    flexDirection: { xs: 'column', sm: 'row' },
    marginBottom: '1rem',
    // Reserve space for the delete column so
    // Tool/Role inputs line up with these fields.
    paddingRight: { xs: 0, sm: '3rem' },
  },
  field: {
    flex: 1,
  },
  errorBox: {
    marginTop: '1rem',
    marginBottom: '1rem',
    backgroundColor: tokens.colors.warning.secondary,
    padding: '1rem',
    borderRadius: '0.25rem',
    width: '100%',
    boxSizing: 'border-box',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1rem',
    marginTop: '2rem',
  },
}

const UserAssignmentModal = ({
  open,
  onClose,
  onSuccess,
  onError,
}: UserAssignmentModalProps) => {
  const { t } = useTranslation()

  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showAssignmentValidation, setShowAssignmentValidation] =
    useState(false)
  const [areAssignmentsValid, setAreAssignmentsValid] = useState(false)

  // SDK assignment type
  const [assignments, setAssignments] = useState<ApiAssignmentViewModel[]>([])

  const methods = useForm<UserAssignmentFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      jobTitle: '',
    },
  })

  const { handleSubmit, reset } = methods

  useEffect(() => {
    if (!open) {
      setHasError(false)
      setErrorMessage('')
      setIsSubmitting(false)
      setAssignments([])
      setShowAssignmentValidation(false)
      setAreAssignmentsValid(false)
      reset()
    }
  }, [open, reset])

  const handleFormSubmit = async (data: UserAssignmentFormData) => {
    setHasError(false)
    setErrorMessage('')
    setShowAssignmentValidation(true)

    if (!areAssignmentsValid) {
      return
    }

    setIsSubmitting(true)

    try {
      const apiAssignments: ApiAssignmentViewModel[] | undefined =
        assignments.length > 0
          ? assignments.map((a) => ({
              assignmentId: a.assignmentId,
              toolId: a.toolId,
              roleId: a.roleId,
            }))
          : undefined

      // SDK model
      const model: ApiUserAssignmentsViewModel = {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim(),
        jobTitle: data.jobTitle?.trim() || undefined,
        assignments: apiAssignments,
      }

      await api.user.clientAdmin_AddUserAssignments(model)

      onSuccess()
      onClose()
    } catch (error: any) {
      const domainErrorMessage = t('UserManagement.AddUser.DomainNotAllowed')
      const genericErrorMessage = t('UserManagement.AddUser.ErrorMessage')

      const backendError =
        error?.data?.data?.error?.exceptionMessage ||
        error?.data?.message ||
        error?.message ||
        null

      // Decide if this is the specific domain error
      const isDomainError = backendError === domainErrorMessage

      setHasError(true)
      setErrorMessage(isDomainError ? domainErrorMessage : genericErrorMessage)

      onError()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInvalidSubmit = () => {
    // clear any top-level API error
    setHasError(false)
    setErrorMessage('')

    // tell UserAssignment to show its validation errors
    setShowAssignmentValidation(true)
  }

  const handleCancel = () => {
    reset()
    setHasError(false)
    setErrorMessage('')
    setAssignments([])
    setShowAssignmentValidation(false)
    setAreAssignmentsValid(false)
    onClose()
  }

  const validateNameField = (value: string, type: 'firstName' | 'lastName') => {
    const trimmed = (value ?? '').trim()
    if (!trimmed) {
      return t(
        type === 'firstName'
          ? 'UserManagement.AddUser.FirstNameRequired'
          : 'UserManagement.AddUser.LastNameRequired',
      )
    }

    // Allow letters, apostrophes, hyphens, and spaces; must contain at least one letter
    const isValidName =
      /^[a-zA-Z' -]+$/.test(trimmed.replace(/\s+/g, ' ')) &&
      /[a-zA-Z]/.test(trimmed)

    if (!isValidName) {
      return t(
        type === 'firstName'
          ? 'UserManagement.AddUser.InvalidFirstName'
          : 'UserManagement.AddUser.InvalidLastName',
      )
    }

    return true
  }

  const errorRef = React.useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (hasError && errorRef.current) {
      errorRef.current.focus()
    }
  }, [hasError])

  return (
    <ResponsiveModal
      open={open}
      onClose={handleCancel}
      desktopMaxWidth="60rem"
      aria-labelledby="add-user-modal-title">
      <Box sx={styles.modalContainer}>
        <Typography
          id="add-user-modal-title"
          sx={styles.header}
          data-testid="Lbl_AddUserModal_Header">
          {t('UserManagement.AddUser.ModalTitle')}
        </Typography>

        <Typography
          sx={styles.subheader}
          data-testid="Lbl_AddUserModal_Subheader">
          {t('UserManagement.AddUser.ModalSubheader')}
        </Typography>

        {hasError && (
          <Box
            ref={errorRef}
            sx={styles.errorBox}
            role="alert"
            aria-live="polite"
            tabIndex={-1}>
            <Typography color="error">{errorMessage}</Typography>
          </Box>
        )}

        <FormProvider {...methods}>
          <form
            style={{ width: '100%', marginTop: '0rem' }}
            onSubmit={handleSubmit(handleFormSubmit, handleInvalidSubmit)}>
            <Box sx={styles.formContainer}>
              {/* Name Row */}
              <Box sx={styles.fieldRow}>
                <Box sx={styles.field}>
                  <TextField
                    labelName="UserManagement.AddUser.FirstName"
                    fieldName="firstName"
                    componentName="AddUserModal"
                    fieldRequired
                    maxLength={50}
                    fieldValidation={(value: string) =>
                      validateNameField(value, 'firstName')
                    }
                  />
                </Box>

                <Box sx={styles.field}>
                  <TextField
                    labelName="UserManagement.AddUser.LastName"
                    fieldName="lastName"
                    componentName="AddUserModal"
                    fieldRequired
                    maxLength={50}
                    fieldValidation={(value: string) =>
                      validateNameField(value, 'lastName')
                    }
                  />
                </Box>
              </Box>

              {/* Email + Job Title */}
              <Box sx={styles.fieldRow}>
                <Box sx={styles.field}>
                  <TextField
                    labelName="UserManagement.AddUser.EmailAddress"
                    fieldName="email"
                    componentName="AddUserModal"
                    fieldRequired
                    maxLength={100}
                    fieldValidation={(value: string) => {
                      const trimmed = (value ?? '').trim()
                      if (!trimmed) {
                        return t('UserManagement.AddUser.EmailRequired')
                      }

                      const emailRegex =
                        /^[A-Za-z0-9](\.?[A-Za-z0-9_\-+%])*@[A-Za-z0-9-]+(\.[A-Za-z]{2,})+$/

                      if (!emailRegex.test(trimmed)) {
                        return t('UserManagement.AddUser.InvalidEmail')
                      }

                      return true
                    }}
                  />
                </Box>

                <Box sx={styles.field}>
                  <TextField
                    labelName="UserManagement.AddUser.JobTitle"
                    fieldName="jobTitle"
                    componentName="AddUserModal"
                    fieldRequired={false}
                    maxLength={100}
                    fieldValidation={(value: string) => {
                      const trimmed = (value ?? '').trim()
                      if (trimmed && !/^(?!\s+$)[a-zA-Z0-9 ]*$/.test(trimmed)) {
                        return t('UserManagement.AddUser.InvalidJobTitle')
                      }

                      return true
                    }}
                  />
                </Box>
              </Box>

              {/* Assignments Component */}
              <Box sx={{ marginTop: '1.5rem' }}>
                <UserAssignment
                  userEmail=""
                  onChange={(newAssignments) => setAssignments(newAssignments)}
                  showValidationErrors={showAssignmentValidation}
                  onValidityChange={(isValid) =>
                    setAreAssignmentsValid(isValid)
                  }
                  loadExistingAssignments={false}
                />
              </Box>
            </Box>

            <Box sx={styles.buttonContainer}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isSubmitting}>
                {t('UserManagement.AddUser.Cancel')}
              </Button>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {isSubmitting && (
                  <CircularProgress size={18} sx={{ color: 'white' }} />
                )}
                {t('UserManagement.AddUser.AddUserButton')}
              </Button>
            </Box>
          </form>
        </FormProvider>
      </Box>
    </ResponsiveModal>
  )
}

export default UserAssignmentModal
